import React, {Component} from "react";
import {Link} from "react-router-dom";
import {Field, reduxForm} from "redux-form";
import PropTypes from "prop-types";
import connect from "react-redux/es/connect/connect";
import {FormattedMessage, injectIntl} from "react-intl";
import {BeatLoader} from 'react-spinners';
import Select from 'react-select'
import filter from "lodash/filter";
import ReactTextareaAutocomplete from "@webscopeio/react-textarea-autocomplete";
import {validate as uuidValidate} from 'uuid';

// @mui/material components
import {
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    AccordionActions,
    Fab,
    Popover,
    Stepper,
    Step,
    StepButton,
    Tooltip,
} from "@mui/material";

// @mui/icons-material
import {
    Add,
    ArrowBackIos,
    ArrowForwardIos,
    Delete,
    ExpandMore,
    KeyboardArrowDown,
    KeyboardArrowUp,
    Remove,
    Settings,
} from "@mui/icons-material";

// core components
import Button from "components/button/button";
import Card from "components/card/card";
import CardBody from "components/card/cardBody";
import GridContainer from "components/grid/gridContainer";
import GridItem from "components/grid/gridItem";
import MultiChoiceDialog from './multiChoiceDialog';

// styles
import formShowStyle from "assets/jss/views/feed/mapper/formStyle";
import {primaryColor, dangerColor} from "assets/jss/main";
import "@webscopeio/react-textarea-autocomplete/style.css";

// actions
import {retrieve} from "actions/feed/update";
import {del} from "actions/feed/mapper/delete";

// helpers
import {capitalize} from "utils/capitalize";
import {fetch} from "../../../../../utils/dataAccess";
import {withStyles} from "@mui/styles";
import {getTranslation} from 'domain/helpers/translations';
import {
    getAdditionalMapperFieldsNamesFromConfiguration,
    getCharacteristicsMapperFields, getInventoryMapperFieldsNamesFromConfiguration,
    getMerchandisingMapperFields,
    getOptionsMapperFields,
    getPricesAndPromotionsMapperFields,
    getStocksAndStatusMapperFields,
    MapperFields, MapperPrefixCode
} from "utils/mapperFields";

import list from 'api/option/list';
import listAttributes from 'api/attribute/list';
import {FeedType} from "../../../../../utils/FeedType";

class Form extends Component {
    static propTypes = {
        handleSubmit: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = {
            activeStep: 0,
            isFirstTimeMapper: false,
            isCombinationPopupOpen: false,
            expertModePosition: null,
            expertModePropertySelected: null,
            expertModePropertyValue: null,
            expertModeEntries: [{ name: "", value: "" }],
            expressionLanguageSuccess: null,
            expressionLanguageError: null,
            expressionLanguageLoading: null,
            options: [],
            attributes: [],
            loading: true,
        };
    }

    componentDidMount() {
        this.props.retrieve(decodeURIComponent(this.props.match.params.fid));

        list('order[position]=asc').then((res) => {
            this.setState({
                options: res,
            })
        })

        listAttributes().then((res) => {
            this.setState({
                attributes: res,
            })
        })

        this.setState({
            loading: false,
        })
    }

    del = () => {
        if (window.confirm("Are you sure you want to delete this item?"))
            this.props.del(this.props.mapperRetrieved);
    };

    onDragEnd = (result, draggableOptions, event) => {
        const {initialValues, change} = this.props;

        if (event.destination && event.source.droppableId === "combineds") {
            if (event.destination.droppableId === "combineds") {
                let source = result[event.source.index];
                result.splice(event.source.index, 1);
                result.splice(event.destination.index, 0, source);
            } else {
                result.splice(event.source.index, 1);
            }
        }

        if (event.destination && event.source.droppableId === "options") {
            if (event.destination.droppableId === "combineds") {
                let src = draggableOptions[event.source.index];
                result.splice(event.destination.index, 0, src);
            }
        }

        let combinedValue = "";
        result.forEach(value => {
            combinedValue += `${value.label}>`;
        });

        combinedValue = combinedValue.substring(0, combinedValue.length - 1);
        change(`configuration[categories].value`, combinedValue);
        change(`configuration[categories].type`, "category-formula");
        initialValues.configuration["categories"].value = combinedValue;
        initialValues.configuration["categories"].type = "category-formula";

        if (!combinedValue.length) {
            this.closeCombinationPopup();
            initialValues.configuration["categories"].type = "category";
            change(`configuration[categories].type`, "category");
        }
    };

    handleStep = step => () => {
        const {initialValues, intl, feedType} = this.props;
        const {activeStep} = this.state;

        if (activeStep === 0 && (initialValues && !((initialValues.configuration.sku?.value || initialValues.configuration.barcode?.value))) && feedType !== FeedType.ENRICHMENT) {
            alert(intl.formatMessage({id: "mapper.step.alert.requiredSku"}));

            return false;
        }
        this.setState({activeStep: step}, () => {
            if (this.state.activeStep === 4) {
                this.setState({isFirstTimeMapper: false})
            }
        })
    };

    openCombinationPopup() {
        this.setState({isCombinationPopupOpen: true});
    }

    closeCombinationPopup = () => {
        this.setState({isCombinationPopupOpen: false});
    }

    getFeedHeaders(feedProperties, propertyName, isExpertMode = false) {
        let options = [];

        if (isExpertMode === false) {
            options.push({
                value: "none",
                label: this.props.intl.formatMessage({id: "mapper.form.options.none"}),
                input: propertyName
            });
        }

        feedProperties && feedProperties.forEach((item) => (
            options.push({
                value: item,
                label: capitalize(item),
                input: propertyName
            })
        ));

        return options;
    }

    handleInputChange = (e, index) => {
        const { name, value } = e.target;
        const list = [...this.state.expertModeEntries];
        list[index][name] = value;
        this.setState({expertModeEntries: list});
    };

    handleRemoveClick = index => {
        const list = [...this.state.expertModeEntries];
        list.splice(index, 1);
        this.setState({expertModeEntries: list});
    };

    handleAddClick = () => {
        this.setState({expertModeEntries: [...this.state.expertModeEntries, { name: "", value: "" }]});
    };

    checkExpressionLanguage = (expression, entries) => {
        this.setState({expressionLanguageLoading: true});
        let finalEntries = {};
        entries.forEach((e) => finalEntries[e.name] = e.value);
        fetch("/expressions-language/check", {method: "POST", body: JSON.stringify({'expression': expression, 'entries': finalEntries})})
            .then(response => {
                response.json().then(retrieved => {
                    this.setState({
                        expressionLanguageSuccess: retrieved.value,
                        expressionLanguageError: retrieved.error,
                        expressionLanguageLoading: false
                    });
                });
            });
    }

    renderField = data => {
        const {classes} = this.props;
        data.input.className = "";

        const isInvalid = data.meta.touched && !!data.meta.error;
        if (isInvalid) {
            data.input.className += " is-invalid";
            data.input["aria-invalid"] = true;
        }

        if (this.props.error && data.meta.touched && !data.meta.error) {
            data.input.className += " is-valid";
        }

        if (data.input.value === "") {
            data.input.value = data.defaultValue ? data.defaultValue : "";
        }
        const lineColor = data.defaultValue ? primaryColor : dangerColor;

        let options = this.getFeedHeaders(data.options, data.input.name);

        let isCombinedValue = data.input.name === "categories" && data.defaultValue && (data.defaultValue.split(">").length > 1)

        const expertModePropertiesBlackList = ["sku", "parentSku", "upc", "barcode"];

        return (
            <Card className={classes.fieldContainer}>
                <CardBody>
                    <GridContainer>
                        <GridItem xs={4} sm={4}>
                            <label
                                htmlFor={`mapper_${data.input.name}`}
                                className={classes.fieldMappingLabel}
                            >
                                {data.label}
                            </label>
                            {data.subTitle &&
                                <span className={classes.fieldMappingSubTitle}> {data.subTitle}</span>
                            }
                        </GridItem>
                        <GridItem className={classes.lineContainer} xs={2} sm={4}>
                            {data.defaultValue ?
                                <FormattedMessage id={'mapper.label.configured'}/> :
                                <FormattedMessage id={'mapper.label.notConfigured'}/>
                            }
                            <div className={classes.line} style={{borderBottom: '2px solid' + lineColor}}>
                                <div>
                                    <div className={classes.round} style={{background: lineColor}}/>
                                    <div className={classes.round} style={{background: lineColor}}/>
                                </div>
                            </div>
                        </GridItem>
                        <GridItem xs={6} sm={4} className={classes.spaceEvenlyGrid}>
                            {expertModePropertiesBlackList.includes(data.input.name) === false &&
                                <GridItem xs={3} sm={3}>
                                    <div>
                                        <Fab className={classes.listButton}>
                                            <Settings onClick={(e) => {
                                                this.openExpertMode(e.currentTarget, data)
                                                }}
                                            />
                                        </Fab>
                                    </div>
                                </GridItem>
                            }
                            <GridItem xs={3} sm={3}>
                                <div>
                                    <Fab onClick={() => this.listFocused(data.input.name)}
                                         className={classes.listButton}
                                         color="default">
                                        {!data.selectedLabel ?
                                            <KeyboardArrowDown/>
                                            :
                                            <KeyboardArrowUp/>
                                        }
                                    </Fab>
                                </div>
                            </GridItem>
                            {data.multiChoice && data.defaultValue && !data.selectedLabel &&
                                <GridItem xs={3} sm={3}>
                                    <Fab onClick={() => this.openCombinationPopup()} className={classes.listButton}>
                                        <Add />
                                    </Fab>
                                </GridItem>
                            }
                            <GridItem xs={9} sm={9}>
                                {data.selectedLabel &&
                                    <Select
                                        options={options}
                                        placeholder={<FormattedMessage id={'mapper.select.placeholder'}/>}
                                        id={`mapper_${data.input.name}`}
                                        onChange={this.handleChange}
                                        defaultMenuIsOpen={true}
                                        className={classes.selectField}
                                        autoFocus={true}
                                        onBlur={() => this.onBlurSelect(data.input.name)}
                                    />
                                }
                                {!data.selectedLabel && !isCombinedValue &&
                                    <b className={classes.defaultValueText}>
                                        {data.defaultValue && (data.expert === '1' ?
                                            <FormattedMessage id={'mapper.label.expert'}/>
                                            : capitalize(data.defaultValue))}
                                    </b>
                                }
                                {!data.selectedLabel && isCombinedValue &&
                                    <Tooltip title={data.defaultValue} placement="top-start">
                                        <b className={classes.defaultValueText}>
                                            {data.defaultValue && (data.expert === '1' ?
                                                <FormattedMessage id={'mapper.label.expert'}/>
                                                : <FormattedMessage id={'mapper.label.combination'}/>)
                                            }
                                        </b>
                                    </Tooltip>
                                }
                            </GridItem>
                        </GridItem>
                    </GridContainer>
                </CardBody>
            </Card>
        );
    };

    openExpertMode(position, property) {
        this.setState({
            expertModePosition: position,
            expertModePropertySelected: property,
            expertModePropertyValue: property.defaultValue ? property.defaultValue: null,
        })
    }

    closeExpertMode() {
        this.setState({
            expertModePosition: null,
            expertModePropertySelected: null,
            expertModePropertyValue: null,
            expertModeEntries: [{ name: "", value: "" }],
            expressionLanguageSuccess: null,
            expressionLanguageError: null,
            expressionLanguageLoading: null,
        });
    }

    onBlurSelect(name) {
        const {initialValues, change} = this.props;
        change(`configuration[${name}].selectedLabel`, false);
        initialValues.configuration[name].selectedLabel = false;
    }

    listFocused(name) {
        const {initialValues, change} = this.props;
        change(`configuration[${name}].selectedLabel`, !initialValues.configuration[name].selectedLabel);
        initialValues.configuration[name].selectedLabel = !initialValues.configuration[name].selectedLabel;
    }

    handleChange = (item, isExpertMode = false, expertModeValue = null) => {
        const {initialValues, change} = this.props;
        const propertyName = (isExpertMode && expertModeValue !== null) ? item.input.name : item.input;
        const mappingValue = (isExpertMode && expertModeValue !== null) ? expertModeValue : item.value;

        initialValues.configuration[propertyName].selectedLabel = false;
        change(`configuration[${propertyName}].selectedLabel`, false);

        if (item.input === "categories") {
            change(`configuration[${propertyName}].type`, 'category');
        }

        if (propertyName.indexOf(MapperPrefixCode.OPTION_VALUE_MAPPING) !== -1) {
            change(`configuration[${propertyName}].type`, 'string');
        }

        if (item.value !== 'none' && mappingValue !== '') {
            change(`configuration[${propertyName}].value`, mappingValue);
            initialValues.configuration[propertyName].value = mappingValue;
        } else {
            change(`configuration[${propertyName}].value`, null);
            initialValues.configuration[propertyName].value = null;
        }

        if (isExpertMode === true && mappingValue !== '') {
            change(`configuration[${propertyName}].expert`, '1');
            initialValues.configuration[propertyName].expert = '1';

            this.closeExpertMode();
        } else {
            change(`configuration[${propertyName}].expert`, '0');
            initialValues.configuration[propertyName].expert = '0';
        }
    };

    formatFieldsLabel(groupedMapperFields, initialValues) {
        const formattedMapperFields = {}

        if (!initialValues || !initialValues.configuration) {
            return formattedMapperFields;
        }

        for (const field of groupedMapperFields ) {
            if (undefined === initialValues.configuration[field]) {
                initialValues.configuration[field] = {
                    value: null,
                    expert: '0',
                    selectedLabel: false,
                    type: 'string',
                };
            } else if (undefined === initialValues.configuration[field].type) {
                initialValues.configuration[field].type = 'string';
            }

            if (initialValues.configuration[field]) {
                formattedMapperFields[field] = {
                    data: initialValues.configuration[field],
                    subTitle: null
                }

                if (field === MapperFields.CATEGORIES) {
                    formattedMapperFields[field].multiChoice = true;
                }
            }
        }

        return formattedMapperFields;
    }

    getLabel(activeStep, initialValues) {
        if (initialValues) {
            switch (activeStep) {
                case 0:
                    return this.formatFieldsLabel(getMerchandisingMapperFields(), initialValues);
                case 1:
                    return this.formatFieldsLabel(getCharacteristicsMapperFields(this.state.options), initialValues);
                case 2:
                    const stockAndStatusFields = this.formatFieldsLabel(getStocksAndStatusMapperFields(), initialValues);
                    const inventoryFields = this.formatFieldsLabel(
                        getInventoryMapperFieldsNamesFromConfiguration(initialValues.configuration),
                        initialValues,
                    );

                    return {
                        ...inventoryFields,
                        ...stockAndStatusFields,
                    }
                case 3:
                    return this.formatFieldsLabel(getPricesAndPromotionsMapperFields(), initialValues);
                default:
                    const additionalMapperFields = this.formatFieldsLabel(
                        getAdditionalMapperFieldsNamesFromConfiguration(initialValues.configuration, this.state.options),
                        initialValues,
                    );
                    let optionsMapperFields = this.formatFieldsLabel(getOptionsMapperFields(), initialValues);

                     if (this.props.channels.length < 2) {
                         delete optionsMapperFields[MapperFields.CHANNELS];
                     }

                    return {
                        ...optionsMapperFields,
                        ...additionalMapperFields
                    }
            }
        }
    }

    firstTimeMapper(initialValues) {
        const isFirstTime = initialValues && filter(initialValues.configuration, (label) => label.value);

        if (isFirstTime && !isFirstTime.length && !this.state.isFirstTimeMapper) {
            this.setState({isFirstTimeMapper: true});
        }

        return isFirstTime;
    }

    renderLabelByFieldName(item) {
        if (uuidValidate(item)) {
            const option = this.getOptionFromId(item)

            if (option) {
                return getTranslation(option).name;
            }

            return item;
        } else if (item.indexOf('attr_') !== -1) {
            const attribute = this.state.attributes?.find((attr) => attr.codeForMapper === item);

            if (attribute) {
                return getTranslation(attribute).name;
            }

            return item;
        } else if (item.indexOf(MapperPrefixCode.INVENTORY) !== -1) {
            const inventory = this.props.inventories?.find((inventory) => inventory.codeForMapper === item);

            if (inventory) {
                return <FormattedMessage id={'mapper.form.label.inventories'} values={{'name': getTranslation(inventory).name}} />;
            }
        } else if (item.indexOf(MapperPrefixCode.OPTION_VALUE_MAPPING) !== -1) {
            const optionId = item.replace(MapperPrefixCode.OPTION_VALUE_MAPPING, '');
            const option = this.getOptionFromId(optionId);

            if (option) {
                return <FormattedMessage id={'mapper.form.label.manualMapping.title'} values={{'name': getTranslation(option).name}} />;
            }

            return item;
        } else {
            return <FormattedMessage id={'mapper.form.label.' + item} />;
        }
    }

    getOptionFromId = (id) => {
        return this.state.options.find((option) => option.id === id);
    }

    render() {
        const {classes, initialValues, retrieved, intl, feedType} = this.props;
        const {activeStep, isFirstTimeMapper, loading} = this.state;

        const labels = this.getLabel(activeStep, loading ? undefined: initialValues);
        const expertModeFeedHeaders = this.getFeedHeaders(
            retrieved && retrieved["properties"],
            (this.state.expertModePropertySelected && this.state.expertModePropertySelected.input.name),
            true
        )

        const steps = [
            intl.formatMessage({id: "mapper.step.label.merchandising"}),
            intl.formatMessage({id: "mapper.step.label.characteristics"}),
            intl.formatMessage({id: "mapper.step.label.stocksAndStatus"}),
            intl.formatMessage({id: "mapper.step.label.pricesAndPromotions"}),
            intl.formatMessage({id: "mapper.step.label.options"})
        ];

        const renderLabel = () => {
            return labels && Object.keys(labels).map((item, key) => {
                if (undefined === labels[item].data) {
                    return;
                }

                const label = this.renderLabelByFieldName(item);
                const isOptionMapping = item.indexOf(MapperPrefixCode.OPTION_VALUE_MAPPING) !== -1;

                return (
                    <div key={key} style={isOptionMapping ? {marginTop: '-15px'} : {}}>
                        {item === "sku" &&
                            <h3>
                                <FormattedMessage id={"mapper.update.mapperObligatorytitle"}/>
                            </h3>
                        }
                        <Field
                            key={key}
                            component={this.renderField}
                            name={item}
                            label={label}
                            type={"select"}
                            options={retrieved && retrieved["properties"]}
                            defaultValue={labels[item].data["value"]}
                            selectedLabel={labels[item].data["selectedLabel"]}
                            multiChoice={labels[item].multiChoice}
                            subTitle={labels[item].subTitle}
                            expert={labels[item].data["expert"]}
                            required
                        />
                        {item === "sku" &&
                            <hr className={classes.separateLine}/>
                        }
                    </div>
                )
            })
        };

        if (activeStep !== 4 || isFirstTimeMapper) {
            this.firstTimeMapper(initialValues);
        }

        let draggableOptions = retrieved && retrieved["properties"].map((option, index) => {
            return {id: `option_${index}`, label: option}
        });

        return (
            <form onSubmit={this.props.handleSubmit}>
                <div className={classes.formContainer}>
                    <Stepper alternativeLabel nonLinear={!isFirstTimeMapper} activeStep={activeStep}>
                        {steps.map((label, index) => {
                            return (
                                <Step key={label} className={index !== 0 ? classes.step : null}>
                                    <StepButton
                                        className={activeStep === index ? classes.selectedStepper : classes.stepper}
                                        onClick={this.handleStep(index)}
                                    >
                                        {label}
                                    </StepButton>
                                </Step>
                            );
                        })}
                    </Stepper>

                    <div className={classes.labelStyle}>
                        {renderLabel()}
                        {this.props.mapperRetrieved && !loading &&
                            <div className={classes.actionsContainer}>
                                <Button
                                    color="info"
                                    simple
                                    component={Link}
                                    to={`../../../..`}
                                >
                                    <ArrowBackIos/>
                                    <FormattedMessage id={"mapper.form.action.back"}/>
                                </Button>

                                {!isFirstTimeMapper ?
                                    <Button color="danger" simple onClick={this.del}>
                                        <Delete/>
                                        <FormattedMessage id={"mapper.update.action.delete"}/>
                                        <div className={classes.submitLoading}>
                                            <BeatLoader
                                                sizeUnit={"px"}
                                                size={4}
                                                color={dangerColor}
                                                loading={this.props.deleteLoading}
                                            />
                                        </div>
                                    </Button>
                                    :
                                    <Button
                                        color="primary"
                                        disabled={activeStep === 0}
                                        onClick={this.handleStep(activeStep - 1)}
                                    >
                                        <ArrowBackIos/>
                                        <FormattedMessage id={"mapper.step.button.previousStep"}/>
                                    </Button>
                                }

                                {!isFirstTimeMapper &&
                                <Button
                                    round
                                    color="success"
                                    type="submit"
                                    disabled={initialValues && (!((initialValues.configuration.sku?.value || initialValues.configuration.barcode?.value)) && feedType !== FeedType.ENRICHMENT)}
                                >
                                    <FormattedMessage id={"mapper.form.action.submit"}/>
                                    <div className={classes.submitLoading}>
                                        <BeatLoader
                                            sizeUnit={"px"}
                                            size={4}
                                            color={"#FFF"}
                                            loading={this.props.updateLoading}
                                        />
                                    </div>
                                </Button>
                                }

                                {isFirstTimeMapper &&
                                <Button color="primary" onClick={this.handleStep(activeStep + 1)}>
                                    <FormattedMessage id={"mapper.step.button.nextStep"}/>
                                    <ArrowForwardIos/>
                                </Button>
                                }
                            </div>
                        }
                    </div>
                    {this.state.isCombinationPopupOpen &&
                        <MultiChoiceDialog
                            classes={classes}
                            onDragEnd={this.onDragEnd}
                            defaultValue={labels && labels["categories"] && labels["categories"].data["value"]}
                            options={draggableOptions}
                            open={this.state.isCombinationPopupOpen}
                            handleModalClose={() => this.closeCombinationPopup()}
                        />
                    }

                    <Popover
                        open={Boolean(this.state.expertModePosition)}
                        anchorEl={this.state.expertModePosition}
                        onClose={() => this.closeExpertMode()}
                        classes={{
                            paper: classes.expertModePopover,
                        }}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <b className={classes.propertyTitle}>
                            {this.state.expertModePropertySelected !== null &&
                                this.renderLabelByFieldName(this.state.expertModePropertySelected.input.name)
                            } :
                        </b>
                        <ReactTextareaAutocomplete
                            className="my-textarea"
                            style={{
                                fontSize: "16px",
                                lineHeight: "20px",
                                padding: 5,
                                fontFamily: "Roboto",
                                height: "150px"
                            }}
                            containerStyle={{
                                margin: "10px",
                                width: "unset"
                            }}
                            value={this.state.expertModePropertyValue}
                            ref={rta => {this.rta = rta}}
                            innerRef={textarea => { this.textarea = textarea}}
                            loadingComponent={() => <span>{intl.formatMessage({id: "mapper.label.expert.loading"})}</span>}
                            onChange={e => this.setState({expertModePropertyValue: e.target.value})}
                            trigger={{
                                "#": {
                                    dataProvider: query => {
                                        return expertModeFeedHeaders.filter(
                                            headers => headers.value.includes(query)
                                        )
                                    },
                                    component: ({ entity: { label } }) => label,
                                    // react-textarea-autocomplete splices the token in via
                                    // `textareaValue.replace(textToModify, modifiedText)` with a
                                    // plain string, so `$` sequences in modifiedText are interpreted
                                    // as replacement patterns (e.g. "$$" collapses to a single "$").
                                    // Pre-doubling every "$" here cancels that out so column names
                                    // containing "$" are inserted unchanged.
                                    output: (item, trigger) => item.value.replace(/\$/g, "$$$$"),
                                    allowWhitespace: true
                                }
                            }}
                        />
                        <Accordion className={classes.accordionExpertMode} defaultExpanded>
                            <AccordionSummary
                                expandIcon={<ExpandMore/>}
                                aria-controls="panel1c-content"
                                id="panel1c-header"
                                className={classes.accordionSummaryExpertMode}
                            >
                                <div className={classes.accordionSummaryExpandedExpertMode}>
                                    <FormattedMessage id={"mapper.expertMode.button.test"}/>
                                </div>
                            </AccordionSummary>
                            <AccordionDetails className={classes.accordionDetailsExpertMode}>
                                <div>
                                    <FormattedMessage id={"mapper.expertMode.notice.entries"}/>
                                </div>
                                <div>
                                    {this.state.expertModeEntries.map((entry, i) => {
                                        return (
                                            <div className={classes.expertModeEntriesBox} key={i}>
                                                <input
                                                    name="name"
                                                    className={classes.expertModeEntriesBoxInput}
                                                    placeholder={this.props.intl.formatMessage({id:"mapper.expertMode.box.placeholder.name"})}
                                                    value={entry.name}
                                                    onChange={e => this.handleInputChange(e, i)}
                                                />
                                                <input
                                                    name="value"
                                                    className={classes.expertModeEntriesBoxInput}
                                                    placeholder={this.props.intl.formatMessage({id:"mapper.expertMode.box.placeholder.value"})}
                                                    value={entry.value}
                                                    onChange={e => this.handleInputChange(e, i)}
                                                />
                                                <div>
                                                    {this.state.expertModeEntries.length !== 1 &&
                                                        <Remove className={classes.expertModeEntriesBoxIcon} onClick={() => this.handleRemoveClick(i)}/>
                                                    }
                                                    {this.state.expertModeEntries.length - 1 === i &&
                                                        <Add className={classes.expertModeEntriesBoxIcon} onClick={this.handleAddClick}/>
                                                    }
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </AccordionDetails>
                            {(this.state.expressionLanguageSuccess || this.state.expressionLanguageError) &&
                                <AccordionDetails className={classes.accordionDetailsExpertMode}>
                                    {this.state.expressionLanguageError ?
                                        <div className={classes.expertModeResultError}>
                                            {this.state.expressionLanguageError}
                                        </div>
                                        :
                                        <div className={classes.expertModeResultSuccess}>
                                            {this.state.expressionLanguageSuccess}
                                        </div>
                                    }
                                </AccordionDetails>
                            }
                            <AccordionActions>
                                <Button
                                    simple
                                    color="info"
                                    className={classes.expertModeButton}
                                    children={null}
                                    onClick={() => {
                                        this.checkExpressionLanguage(
                                            this.state.expertModePropertyValue,
                                            this.state.expertModeEntries
                                        )}
                                    }
                                >
                                    {this.state.expressionLanguageLoading ?
                                        <BeatLoader
                                            sizeUnit={"px"}
                                            size={4}
                                            color={primaryColor}
                                            loading={true}
                                        />
                                    :
                                        <FormattedMessage id={"mapper.expertMode.button.test"}/>
                                    }
                                </Button>
                            </AccordionActions>
                        </Accordion>
                        <Divider />
                        <div className={classes.expertModeActionArea}>
                            <Button
                                simple
                                color="info"
                                onClick={() => this.closeExpertMode()}
                                className={classes.expertModeButton}
                            >
                                <FormattedMessage id={"mapper.expertMode.button.cancel"}/>
                            </Button>
                            <Button
                                round
                                color="success"
                                onClick={() => {
                                    this.handleChange(
                                        this.state.expertModePropertySelected,
                                        true,
                                        this.state.expertModePropertyValue
                                    )
                                }}
                                className={classes.expertModeButton}
                            >
                                <FormattedMessage id={"mapper.expertMode.button.confirm"}/>
                            </Button>
                        </div>
                    </Popover>
                </div>
            </form>
        );
    }
}

const mapStateToProps = state => {
    const retrieved = state.feed.update.retrieved;
    const updateLoading = state.mapper.update.updateLoading;
    const deleteLoading = state.mapper.del.loading;
    const mapperRetrieved = state.mapper.update.retrieved;
    const channels = state.currentOrganization.retrieved?.eshopConfig?.channels;
    const inventories = state.currentOrganization.retrieved?.inventories;

    return {
        retrieved,
        updateLoading,
        deleteLoading,
        mapperRetrieved,
        channels,
        inventories,
        initial: state.form.mapper && state.form.mapper.initial && state.form.mapper.initial.configuration,
        values: state.form.mapper && state.form.mapper.values && state.form.mapper.values.configuration,
    };
};

const mapDispatchToProps = dispatch => ({
    retrieve: id => dispatch(retrieve(id)),
    del: item => dispatch(del(item)),
});

export default reduxForm({
    form: "mapper",
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
}, mapStateToProps)(connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(formShowStyle)(injectIntl(Form))));
